/**
 * API response types and error classes.
 *
 * All types used by the Worker API (inbox, conversations, messages,
 * businesses, admin endpoints) are defined here. Mirrors the shapes
 * returned by the Cloudflare Worker backend.
 */

// ---------------------------------------------------------------------------
// Error types
// ---------------------------------------------------------------------------

/**
 * Typed error for non-2xx API responses.
 * Captures the HTTP status and the `x-request-id` header for debugging.
 */
export class ApiError extends Error {
  status: number;
  requestId: string | null;

  constructor(message: string, status: number, requestId: string | null) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.requestId = requestId;
  }
}

/**
 * Typed error for 429 sync-cooldown responses.
 * The UI can use `retryAfter` to display a countdown.
 */
export class SyncCooldownError extends Error {
  retryAfter: number;

  constructor(retryAfter: number) {
    super("sync_cooldown");
    this.name = "SyncCooldownError";
    this.retryAfter = retryAfter;
  }
}

// ---------------------------------------------------------------------------
// Conversation types
// ---------------------------------------------------------------------------

/**
 * A conversation as returned by the Worker API.
 *
 * The inbox endpoint returns rows from the inbox_items_v1 view (which
 * includes last_message_from/snippet/direction but omits some conversation
 * columns like provider). The single-conversation endpoint returns the full
 * conversations row. Optional fields accommodate both shapes.
 */
export interface Conversation {
  id: string;
  business_id: string;
  mailbox_id: string;
  subject: string | null;
  last_message_at: string | null;
  status: "open" | "closed";
  has_attachments?: boolean;
  created_at: string;
  /** Present on single-conversation responses, absent on inbox view rows. */
  provider?: string;
  /** Present on single-conversation responses, absent on inbox view rows. */
  provider_thread_id?: string;
  /** Present on single-conversation responses, absent on inbox view rows. */
  updated_at?: string;
  /** Email address of the mailbox (from mailboxes join, single-conversation only). */
  mailbox_email?: string | null;
  /** Last message sender email (from inbox_items_v1 view). */
  last_message_from?: string | null;
  /** Last message sender display name (from inbox_items_v1 view). */
  last_message_from_display_name?: string | null;
  /** Last message snippet (from inbox_items_v1 view). */
  last_message_snippet?: string | null;
  /** Last message direction: "inbound" | "outbound" (from inbox_items_v1 view). */
  last_message_direction?: string | null;
  /** Assigned user ID (from inbox_items_v2 / single-conversation). */
  assigned_to?: string | null;
  /** Assignment timestamp. */
  assigned_at?: string | null;
  /** Conversation priority. */
  priority?: "low" | "normal" | "high" | "urgent";
  /** Business color (from inbox_items_v2). */
  business_color?: string | null;
  /** Tags attached to this conversation (from inbox_items_v2 view). */
  tags?: ConversationTag[];
  /** Linked contact (T1.1). Null when no contact is linked. */
  contact_id?: string | null;
  /** Watcher user IDs (T1.3). Empty array when no one is watching. */
  watchers?: string[];
  /**
   * Phase 1 virtual person profile: derived from the most recent inbound
   * message. Returned by `GET /api/conversations/:id` regardless of whether
   * a Contact is linked — the frontend prefers Contact data when present but
   * falls back to this for fields the Contact doesn't have. Null when the
   * conversation has no inbound messages (rare outbound-only thread).
   */
  derived_contact?: DerivedContact | null;
}

/**
 * Phase 1 virtual person profile shape — what the conversation GET response
 * carries when there's no Contact row, and what the PersonProfileSidebar
 * uses to populate fields. `first_name` mirrors the same algorithm the
 * frontend's `firstNameOf()` uses (display_name first token, falls back to
 * email local-part).
 */
export interface DerivedContact {
  email: string;
  display_name: string | null;
  first_name: string | null;
}

/** A message as returned by the Worker API. */
export interface Message {
  id: string;
  conversation_id: string;
  direction: "inbound" | "outbound";
  from_email: string | null;
  from_display_name: string | null;
  to_emails: string[];
  cc_emails?: string[];
  bcc_emails?: string[];
  sent_at: string | null;
  received_at: string | null;
  subject: string | null;
  snippet: string | null;
  body_plain: string | null;
  body_html: string | null;
  provider_message_id: string;
  created_at: string;
  attachments: MessageAttachment[];
}

/** An attachment on a message. */
export interface MessageAttachment {
  id: string;
  message_id: string;
  filename: string;
  mime_type: string;
  size_bytes: number;
  content_id: string | null;
  is_inline: boolean;
  r2_key: string | null;
  storage_status: "pending" | "stored" | "failed";
}

// ---------------------------------------------------------------------------
// API response types
// ---------------------------------------------------------------------------

/** Response from GET /api/inbox. */
export interface InboxResponse {
  conversations: Conversation[];
  next_cursor: string | null;
}

/** Response from GET /api/conversations/:id. */
export interface ConversationResponse {
  conversation: Conversation;
}

/** Response from GET /api/conversations/:id/messages. */
export interface MessagesResponse {
  messages: Message[];
  next_cursor: string | null;
}

// ---------------------------------------------------------------------------
// Request param types
// ---------------------------------------------------------------------------

/** Optional query parameters for the inbox listing. */
export interface InboxParams {
  status?: "open" | "closed";
  mailbox_id?: string;
  q?: string;
  /**
   * Phase 1 past-threads lookup: only conversations where any message has
   * lower(from_email) = lower(this value). Case-insensitive. Used by the
   * PersonProfileSidebar past-threads disclosure.
   */
  from_email?: string;
  cursor?: string;
  limit?: number;
}

/** Optional query parameters for message listing. */
export interface MessagesParams {
  cursor?: string;
  limit?: number;
}

/** Body for PATCH /api/conversations/:id. */
export interface ConversationUpdateBody {
  status?: "open" | "closed";
  assigned_to?: string | null;
  priority?: "low" | "normal" | "high" | "urgent";
  /** T3 inbox-row "Contact" quick-action: link / unlink a contact. */
  contact_id?: string | null;
}

// ---------------------------------------------------------------------------
// Business types
// ---------------------------------------------------------------------------

/** A business the user is a member of. */
export interface Business {
  id: string;
  name: string;
  slug: string;
  status: "active" | "inactive";
  role: "owner" | "admin" | "agent" | "viewer";
  color?: string | null;
  created_at: string;
  /** Phase 3: monthly DeepSeek enrichment spend cap, in cents. */
  extraction_cost_cap_cents?: number;
  /** Phase 3: cents spent this period — resets monthly. */
  extraction_spent_cents?: number;
  /** Phase 3: start of the current spend period (ISO ts). */
  extraction_period_start?: string;
}

/** Response from GET /api/businesses. */
export interface BusinessesResponse {
  businesses: Business[];
}

/** A Google connection for a business. */
export interface GoogleConnection {
  id: string;
  business_id: string;
  google_account_email: string | null;
  connection_type: string;
  status: string;
  created_at: string;
}

/** Response from GET /api/businesses/:id/connections. */
export interface BusinessConnectionsResponse {
  connections: GoogleConnection[];
}

/** Body for POST /api/admin/mailboxes. */
export interface CreateMailboxBody {
  business_id: string;
  google_connection_id: string;
  email: string;
}

/** Response from POST /api/admin/mailboxes. */
export interface CreateMailboxResponse {
  ok: boolean;
  mailbox: {
    id: string;
    business_id: string;
    google_connection_id: string;
    email: string;
    provider: string;
    is_active: boolean;
    created_at: string;
  };
  message: string;
}

// ---------------------------------------------------------------------------
// Me (user + businesses with roles)
// ---------------------------------------------------------------------------

/** Response from GET /api/me. */
export interface MeResponse {
  user: { id: string; email: string };
  businesses: Business[];
}

// ---------------------------------------------------------------------------
// Admin types
// ---------------------------------------------------------------------------

/** Health stats returned by GET /api/admin/health. */
export interface HealthStats {
  mailboxes_active_count: number;
  mailboxes_error_count: number;
  messages_last_24h: number;
  sync_failures_last_24h: number;
  last_sync_at: string | null;
  pending_drafts_count: number;
  connectors_error_count: number;
  last_sync_max_age_minutes: number | null;
  sends_last_24h: number;
}

/** Mailbox row returned by GET /api/admin/mailboxes. */
export interface AdminMailbox {
  id: string;
  email: string;
  business_id: string;
  is_active: boolean;
  sync_status: string;
  last_sync_at: string | null;
  cursor_exists: boolean;
  last_error: string | null;
  sync_updated_at: string | null;
}

/** Single event_log row. */
export interface LogEntry {
  id: string;
  level: "debug" | "info" | "warn" | "error";
  event_name: string;
  source: string;
  request_id: string;
  correlation_id: string | null;
  business_id: string | null;
  mailbox_id: string | null;
  job_id: string | null;
  conversation_id: string | null;
  message_id: string | null;
  duration_ms: number | null;
  http_status: number | null;
  error_code: string | null;
  error_message: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
}

/** Single audit_event row. */
export interface AuditEvent {
  id: string;
  business_id: string;
  event_type: string;
  actor_user_id: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
}

/** Filters for the log query endpoint. */
export interface LogFilters {
  correlation_id?: string;
  request_id?: string;
  event_name?: string;
  mailbox_id?: string;
  level?: string;
  since?: string;
  limit?: number;
}

/** Filters for the audit events endpoint. */
export interface AuditFilters {
  business_id?: string;
  event_type?: string;
  since?: string;
  limit?: number;
}

/** Response shape from POST /api/admin/mailboxes. */
export interface CreateMailboxResult {
  ok: boolean;
  mailbox: {
    id: string;
    email: string;
    business_id: string;
    google_connection_id: string;
    provider: string;
    is_active: boolean;
    created_at: string;
  };
  message: string;
}

// ---------------------------------------------------------------------------
// Draft types
// ---------------------------------------------------------------------------

/** Shape of a draft returned by the API. */
export interface Draft {
  id: string;
  conversation_id: string;
  body_plain: string;
  body_html: string | null;
  status: string;
  created_at: string;
  updated_at: string;
}

/** Response from POST /api/drafts. */
export interface SaveDraftResponse {
  draft_id: string;
}

/** Request body for POST /api/drafts. */
export interface SaveDraftInput {
  conversation_id: string;
  body_plain: string;
  body_html?: string | null;
}

/** Response from POST /api/drafts/:id/approve-and-send. */
export interface SendDraftResponse {
  success: boolean;
  message_id: string | null;
  gmail_message_id: string;
  gmail_thread_id: string;
}

/** Error body returned on send failures with reason_code. */
export interface SendErrorBody {
  error: string;
  reason_code?: string;
}

// ---------------------------------------------------------------------------
// OAuth types
// ---------------------------------------------------------------------------

/** Response from POST /api/oauth/start. */
export interface StartOAuthResponse {
  auth_url: string;
}

/** Shape of a connector returned by GET /api/connectors/:id. */
export interface ConnectorHealth {
  id: string;
  business_id: string;
  google_account_email: string | null;
  connection_type: string;
  scopes: string[];
  status: "pending" | "active" | "needs_reauth" | "error" | "revoked";
  last_error: string | null;
  last_token_refresh_at: string | null;
  last_connected_at: string | null;
  created_at: string;
  updated_at: string;
}

// ---------------------------------------------------------------------------
// Tag types (CRM Phase 3)
// ---------------------------------------------------------------------------

/** A tag that can be applied to conversations. */
export interface Tag {
  id: string;
  business_id: string;
  name: string;
  color: string;
  description?: string | null;
  created_at: string;
}

/** Lightweight tag reference (e.g. from inbox view). */
export interface ConversationTag {
  id: string;
  name: string;
  color: string;
}

/** Response from GET /api/tags. */
export interface TagsResponse {
  tags: Tag[];
}

/** Body for POST /api/tags. */
export interface CreateTagInput {
  business_id: string;
  name: string;
  color?: string;
  description?: string;
}

// ---------------------------------------------------------------------------
// Business update types (CRM Phase 3)
// ---------------------------------------------------------------------------

/** Body for PATCH /api/businesses/:id. */
export interface BusinessUpdateBody {
  name?: string;
  color?: string | null;
  /** Phase 3: monthly DeepSeek enrichment spend cap, in cents. */
  extraction_cost_cap_cents?: number;
}

/** Response from PATCH /api/businesses/:id. */
export interface BusinessUpdateResponse {
  business: {
    id: string;
    name: string;
    color: string | null;
    /** Phase 3: present from migration 0033 onwards. */
    extraction_cost_cap_cents?: number;
    extraction_spent_cents?: number;
    extraction_period_start?: string;
  };
}

// ---------------------------------------------------------------------------
// Business members (for assignment picker)
// ---------------------------------------------------------------------------

/** A business member with role, email, and display name. */
export interface BusinessMember {
  user_id: string;
  email: string | null;
  display_name?: string | null;
  role: string;
  created_at: string;
}

export interface BusinessMembersResponse {
  members: BusinessMember[];
}

/** Body for POST /api/businesses/:id/members. */
export interface AddMemberInput {
  email: string;
  role: "viewer" | "agent" | "admin" | "owner";
  initial_password?: string;
  display_name?: string;
}

export interface AddMemberResponse {
  member: BusinessMember;
  was_new_user: boolean;
}

/** Body for PATCH /api/businesses/:bid/members/:uid. */
export interface UpdateMemberInput {
  role?: "viewer" | "agent" | "admin" | "owner";
  display_name?: string | null;
}

export interface UpdateMemberResponse {
  member: BusinessMember;
}

export interface RemoveMemberResponse {
  success: true;
  unassigned_conversations: number;
  unassigned_tasks: number;
}

// ---------------------------------------------------------------------------
// Centralized team (GET /api/team)
// ---------------------------------------------------------------------------

export interface TeamBusiness {
  id: string;
  name: string;
  color: string | null;
}

export interface TeamMember {
  user_id: string;
  email: string | null;
  display_name: string | null;
  /** Role keyed by business_id. A missing business_id = no access to that inbox. */
  roles: Record<string, "viewer" | "agent" | "admin" | "owner">;
}

export interface TeamResponse {
  businesses: TeamBusiness[];
  members: TeamMember[];
}

// ---------------------------------------------------------------------------
// Template types (CRM Phase 3)
// ---------------------------------------------------------------------------

/** An email template. */
export interface Template {
  id: string;
  business_id: string;
  name: string;
  subject: string | null;
  body_plain: string;
  body_html: string | null;
  category: string;
  variables: string[];
  usage_count: number;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

/** Response from GET /api/templates. */
export interface TemplatesResponse {
  templates: Template[];
}

/** Body for POST /api/templates. */
export interface CreateTemplateInput {
  business_id: string;
  name: string;
  subject?: string;
  body_plain: string;
  body_html?: string;
  category?: string;
  variables?: string[];
}

// ---------------------------------------------------------------------------
// Contact types (CRM Phase 3)
// ---------------------------------------------------------------------------

/** Enrichment-driven enums on the Contact row. Mirror of worker schemas. */
export const CONTACT_RELATIONSHIPS = [
  "self",
  "parent",
  "guardian",
  "partner",
  "friend",
  "unknown",
] as const;
export type ContactRelationship = (typeof CONTACT_RELATIONSHIPS)[number];

export const CONTACT_LICENSE_STAGES = [
  "learner",
  "provisional",
  "full",
  "overseas_conversion",
  "unknown",
] as const;
export type ContactLicenseStage = (typeof CONTACT_LICENSE_STAGES)[number];

export const CONTACT_VEHICLE_PREFERENCES = [
  "automatic",
  "manual",
  "unspecified",
] as const;
export type ContactVehiclePreference =
  (typeof CONTACT_VEHICLE_PREFERENCES)[number];

export const CONTACT_PREFERRED_CONTACT_METHODS = [
  "email",
  "sms",
  "phone",
  "unspecified",
] as const;
export type ContactPreferredContactMethod =
  (typeof CONTACT_PREFERRED_CONTACT_METHODS)[number];

/** A contact in the CRM. */
export interface Contact {
  id: string;
  business_id: string;
  email: string;
  display_name: string | null;
  phone: string | null;
  company: string | null;
  notes: string | null;
  metadata: Record<string, unknown>;
  /** T2.1: current pipeline stage. Null = not in pipeline. */
  current_stage_id?: string | null;
  /** T2.1: timestamp of last stage move. Null when never moved. */
  stage_moved_at?: string | null;
  created_at: string;
  updated_at: string;
  /** T1.2: present only on GET /api/contacts/:id responses. */
  conversation_count?: number;
  // ---------------------------------------------------------------------
  // Phase 3 (Session 20) — enrichment columns
  // ---------------------------------------------------------------------
  /** Driver-to-be; may equal display_name when relationship='self'. */
  student_name?: string | null;
  /** How the sender relates to the student. */
  relationship?: ContactRelationship | null;
  /** Licensing stage. */
  license_stage?: ContactLicenseStage | null;
  /** Manual / Automatic preference. */
  vehicle_preference?: ContactVehiclePreference | null;
  /** Preferred channel for replies. */
  preferred_contact_method?: ContactPreferredContactMethod | null;
  /** Free-text "after 3pm weekdays" etc. */
  preferred_contact_time?: string | null;
  /** Street/suburb/state/country joined; no structured columns yet. */
  address?: string | null;
}

/**
 * Enriched contact row returned by GET /api/contacts. Extends the raw
 * Contact with business metadata + conversation-derived fields so the
 * aggregate list can render color-coded rows with assignee avatars and
 * last-activity timestamps without N extra fetches.
 */
export interface ContactListItem extends Contact {
  business_name: string | null;
  business_color: string | null;
  /** Distinct user_ids assigned to any linked conversation. May be empty. */
  assignees: string[];
  /** ISO timestamp of the most recent linked-convo last_message_at. */
  last_activity_at: string | null;
  conversation_count: number;
}

/** Response from GET /api/contacts. */
export interface ContactsResponse {
  contacts: ContactListItem[];
}

/** Activity-window preset accepted by GET /api/contacts. */
export type ContactActivityWindow = "7d" | "30d" | "90d" | "all";

/** Query params for GET /api/contacts (aggregate + filters). */
export interface ContactsQueryParams {
  /** Single-business mode. Omit for aggregate. */
  business_id?: string;
  /** Narrow the aggregate to a subset of the caller's businesses. */
  business_ids?: string[];
  /** Free-text search across display_name, email, phone, company. */
  q?: string;
  /** Filter: contacts with a linked convo assigned to this user_id. */
  assigned_to?: string;
  /** Filter: last linked-convo activity within this window. */
  activity?: ContactActivityWindow;
  /** Row cap (server enforces max 500, default 500). */
  limit?: number;
}

/** Session 21: tag chip shape returned in active_tags. */
export interface ContactActiveTag {
  id: string;
  name: string;
  color: string | null;
}

/** Session 21: cross-business identity hint. */
export interface ContactAlsoIn {
  business_id: string;
  business_name: string;
  business_color: string | null;
  contact_id: string;
}

/** Response from GET /api/contacts/:id. */
export interface ContactResponse {
  contact: Contact & {
    /** Distinct tags across this contact's OPEN linked conversations. */
    active_tags?: ContactActiveTag[];
    /** Other businesses where the caller can see a contact with this email. */
    also_in?: ContactAlsoIn[];
  };
}

/** Response from GET /api/contacts/:id/conversations. */
export interface ContactConversationsResponse {
  conversations: Conversation[];
  next_cursor: string | null;
}

/** Body for POST /api/contacts. */
export interface CreateContactInput {
  business_id: string;
  email: string;
  display_name?: string;
  phone?: string;
  company?: string;
  notes?: string;
  // Phase 3 enrichment columns — all optional.
  student_name?: string | null;
  relationship?: ContactRelationship | null;
  license_stage?: ContactLicenseStage | null;
  vehicle_preference?: ContactVehiclePreference | null;
  preferred_contact_method?: ContactPreferredContactMethod | null;
  preferred_contact_time?: string | null;
  address?: string | null;
}

/** Body for PATCH /api/contacts/:id. Partial. */
export interface UpdateContactInput {
  email?: string;
  display_name?: string | null;
  phone?: string | null;
  company?: string | null;
  notes?: string | null;
  student_name?: string | null;
  relationship?: ContactRelationship | null;
  license_stage?: ContactLicenseStage | null;
  vehicle_preference?: ContactVehiclePreference | null;
  preferred_contact_method?: ContactPreferredContactMethod | null;
  preferred_contact_time?: string | null;
  address?: string | null;
}

// ---------------------------------------------------------------------------
// Pipeline stages (T2.1)
// ---------------------------------------------------------------------------

/** A single configurable pipeline stage for a business. */
export interface PipelineStage {
  id: string;
  business_id: string;
  name: string;
  color: string;
  position: number;
  created_at: string;
  updated_at: string;
}

/** Response from GET /api/pipeline-stages. */
export interface PipelineStagesResponse {
  stages: PipelineStage[];
}

/** Body for POST /api/pipeline-stages. */
export interface CreatePipelineStageInput {
  business_id: string;
  name: string;
  color?: string;
  position?: number;
}

/** Body for PATCH /api/pipeline-stages/:id (partial). */
export interface UpdatePipelineStageInput {
  name?: string;
  color?: string;
  position?: number;
}

/** Body for POST /api/pipeline-stages/reorder. */
export interface ReorderPipelineStagesInput {
  business_id: string;
  stages: Array<{ id: string; position: number }>;
}

/** A single transition row from `contact_stage_transitions`. */
export interface ContactStageTransition {
  id: string;
  contact_id: string;
  from_stage_id: string | null;
  to_stage_id: string | null;
  moved_by: string | null;
  moved_at: string;
}

/** Response from GET /api/contacts/:id/stage-history. */
export interface ContactStageHistoryResponse {
  transitions: ContactStageTransition[];
}

/** Response from PATCH /api/contacts/:id/stage. */
export interface MoveContactStageResponse {
  contact: Contact;
  moved: boolean;
}

// ---------------------------------------------------------------------------
// Tasks (Roadmap T3)
// ---------------------------------------------------------------------------

/** A single task. Task can be standalone, contact-scoped, or conversation-scoped. */
export interface Task {
  id: string;
  business_id: string;
  title: string;
  description: string | null;
  due_at: string | null;
  status: "open" | "done";
  owner_user_id: string | null;
  contact_id: string | null;
  conversation_id: string | null;
  created_by: string | null;
  created_at: string;
  completed_at: string | null;
  updated_at: string;
}

/** Query params for GET /api/tasks. business_id optional (aggregate mode). */
export interface TasksParams {
  business_id?: string;
  owner_user_id?: string;
  status?: "open" | "done";
  contact_id?: string;
  conversation_id?: string;
  /** ISO timestamp: only tasks with due_at strictly before this. */
  due_before?: string;
  cursor?: string;
  limit?: number;
}

/** Response from GET /api/tasks. */
export interface TasksResponse {
  tasks: Task[];
  next_cursor: string | null;
}

/** Response from GET /api/contacts/:id/tasks or /api/conversations/:id/tasks. */
export interface ScopedTasksResponse {
  tasks: Task[];
}

/** Body for POST /api/tasks. */
export interface CreateTaskInput {
  business_id: string;
  title: string;
  description?: string;
  due_at?: string | null;
  owner_user_id?: string | null;
  contact_id?: string | null;
  conversation_id?: string | null;
}

/** Body for PATCH /api/tasks/:id. Partial — at least one field. */
export interface UpdateTaskInput {
  title?: string;
  description?: string | null;
  due_at?: string | null;
  status?: "open" | "done";
  owner_user_id?: string | null;
  contact_id?: string | null;
  conversation_id?: string | null;
}

/** Bucketed open tasks returned by GET /api/me/work. */
export interface AssignedTasksBuckets {
  overdue: Task[];
  today: Task[];
  this_week: Task[];
  no_due_date: Task[];
}

/** Response from GET /api/me/work. */
export interface MeWorkResponse {
  assigned_conversations: Conversation[];
  assigned_tasks: AssignedTasksBuckets;
  watched_conversations: Conversation[];
}

// ---------------------------------------------------------------------------
// /api/me/sync-status (S22) — banner alerts for sync + connection failures
// ---------------------------------------------------------------------------

export type SyncIssueKind = "reauth" | "error" | "stale";

export interface SyncIssue {
  kind: SyncIssueKind;
  mailbox_email: string;
  /** Present only for kind='stale'. */
  minutes?: number;
  /** Last raw error from sync_state — present for 'error' / 'reauth'. */
  message?: string | null;
  /** Last known successful sync; null if the mailbox has never synced. */
  last_sync_at?: string | null;
}

export interface SyncStatusBusiness {
  business_id: string;
  business_name: string;
  business_color: string | null;
  /** Caller's role on this business — drives the Reconnect CTA. */
  role: "viewer" | "agent" | "admin" | "owner";
  issues: SyncIssue[];
}

export interface SyncStatusResponse {
  /** Only businesses that have at least one issue. Empty = all good. */
  businesses: SyncStatusBusiness[];
}

// ---------------------------------------------------------------------------
// Search types (Full-Text Search)
// ---------------------------------------------------------------------------

/** A single search result (message match). */
export interface SearchResult {
  id: string;
  conversation_id: string;
  subject: string | null;
  snippet: string | null;
  from_email: string | null;
  from_display_name: string | null;
  direction: "inbound" | "outbound";
  created_at: string;
  business_id: string;
  conversation_status: "open" | "closed" | null;
}

/** Response from GET /api/search. */
export interface SearchResponse {
  results: SearchResult[];
  query: string;
}

// ---------------------------------------------------------------------------
// Contact notes (T4)
// ---------------------------------------------------------------------------

export interface ContactNote {
  id: string;
  contact_id: string;
  author_user_id: string | null;
  body: string;
  created_at: string;
  updated_at: string;
}

export interface ContactNotesResponse {
  notes: ContactNote[];
}

export interface CreateContactNoteInput {
  body: string;
}

export interface UpdateContactNoteInput {
  body: string;
}

// ---------------------------------------------------------------------------
// Conversation actions (SOP gap 2)
// ---------------------------------------------------------------------------

/** Action codes from the operator runbook — keep in lockstep with
 * worker/src/lib/schemas.ts `CONVERSATION_ACTION_CODES`. */
export const CONVERSATION_ACTION_CODES = [
  "LMVM",
  "LMTCB",
  "CNA",
  "SMS_SENT",
  "EMAIL_SENT",
  "CLW",
  "WAIVED",
  "AAR",
  "AOK",
  "CD",
  "H4",
] as const;

export type ConversationActionCode = (typeof CONVERSATION_ACTION_CODES)[number];

export const CONVERSATION_ACTION_CHANNELS = [
  "call",
  "sms",
  "voicemail",
  "email",
  "none",
] as const;

export type ConversationActionChannel =
  (typeof CONVERSATION_ACTION_CHANNELS)[number];

/**
 * Fallback labels for the default action codes. The live, per-business
 * vocabulary now comes from the `conversation_action_codes` table via
 * `useActionCodes(businessId)` (migration 0039); this map is only a fallback
 * for rendering a historical code that's no longer in the business's list.
 */
export const ACTION_CODE_LABELS: Record<string, string> = {
  LMVM: "Left voicemail",
  LMTCB: "Left message to call back",
  CNA: "Called, no answer",
  SMS_SENT: "SMS sent",
  EMAIL_SENT: "Email sent",
  CLW: "Called live (talked)",
  WAIVED: "Cancellation fee waived",
  AAR: "Added and reconciled",
  AOK: "Finalised, no further action",
  CD: "Confirmation done",
  H4: "Hold for…",
};

export interface ConversationAction {
  id: string;
  conversation_id: string;
  business_id: string;
  actor_user_id: string | null;
  /** Live label resolved per-business via useActionCodes; raw token otherwise. */
  code: string;
  channel: ConversationActionChannel;
  at: string;
  free_text: string | null;
  created_at: string;
}

export interface ConversationActionsResponse {
  actions: ConversationAction[];
}

export interface CreateConversationActionInput {
  code: string;
  channel?: ConversationActionChannel;
  free_text?: string;
  /** ISO timestamp — defaults to now() server-side if omitted. */
  at?: string;
}

// ---------------------------------------------------------------------------
// Conversation action codes — admin-managed vocabulary (migration 0039)
// ---------------------------------------------------------------------------

/** A single admin-managed action code for a business. */
export interface ActionCode {
  id: string;
  business_id: string;
  code: string;
  label: string;
  default_channel: ConversationActionChannel;
  position: number;
  created_at: string;
  updated_at: string;
}

/** Response from GET /api/action-codes. */
export interface ActionCodesResponse {
  codes: ActionCode[];
}

/** Body for POST /api/action-codes. */
export interface CreateActionCodeInput {
  business_id: string;
  code: string;
  label: string;
  default_channel?: ConversationActionChannel;
  position?: number;
}

/** Body for PATCH /api/action-codes/:id (partial). */
export interface UpdateActionCodeInput {
  code?: string;
  label?: string;
  default_channel?: ConversationActionChannel;
  position?: number;
}

/** Body for POST /api/action-codes/reorder. */
export interface ReorderActionCodesInput {
  business_id: string;
  codes: Array<{ id: string; position: number }>;
}

// ---------------------------------------------------------------------------
// Phase 2 — message extractions (heuristic adapter; Phase 3 adds DeepSeek)
// ---------------------------------------------------------------------------

/**
 * Extraction kinds. Phase 2 heuristics emit the first five; Phase 3
 * DeepSeek emits the rest. Mirror of worker/src/lib/llmProvider.ts.
 *
 * `key_dates` is multi-value: the worker stores rows with value formatted
 * as "label|YYYY-MM-DD"; the sidebar parses these back into structured
 * pairs for rendering.
 */
export type ExtractionKind =
  // Phase 2 heuristics:
  | "phone"
  | "email"
  | "address"
  | "name_from_signature"
  | "instructor_name"
  // Phase 3 LLM-only:
  | "student_name"
  | "guardian_name"
  | "relationship"
  | "lesson_date"
  | "test_date"
  | "lesson_time"
  | "vehicle_preference"
  | "license_stage"
  | "service_type"
  | "intent_class"
  | "urgency"
  | "payment_status"
  | "preferred_contact_method"
  | "preferred_contact_time"
  | "booking_reference"
  | "key_dates"
  | "requested_action"
  // Phase 3 deferred (placeholder kept for back-compat):
  | "suggested_action";

export type ExtractionConfidence = "high" | "medium" | "low";

export interface MessageExtraction {
  id: string;
  message_id: string;
  business_id: string;
  kind: ExtractionKind;
  value: string;
  confidence: ExtractionConfidence;
  source: string;
  model_version: string;
  extracted_at: string;
}

/** Response shape from GET /api/conversations/:id/extractions. */
export interface ConversationExtractionsResponse {
  extractions: Partial<Record<ExtractionKind, MessageExtraction[]>>;
}

/**
 * Response shape from POST /api/conversations/:id/enrich (Phase 3).
 * Extends the GET shape with the post-call cost meta so the Enrich card
 * can render "spent / cap" without a separate fetch.
 */
export interface EnrichConversationResponse extends ConversationExtractionsResponse {
  cost_cents: number;
  spent_cents: number;
  cap_cents: number;
  inserted: number;
  produced: number;
  model_version: string;
}

// ---------------------------------------------------------------------------
// Session 21 — Contact activity timeline + extractions summary
// ---------------------------------------------------------------------------

export type ContactActivityKind =
  | "message"
  | "audit"
  | "conversation_action"
  | "stage_transition"
  | "note"
  | "task_created"
  | "task_completed";

export interface ContactActivityItem {
  kind: ContactActivityKind;
  occurred_at: string;
  actor_user_id: string | null;
  conversation_id: string | null;
  message_id: string | null;
  task_id: string | null;
  note_id: string | null;
  /** Present when kind === 'audit'. */
  event_type?: string;
  /** Present when kind === 'conversation_action'. */
  action_code?: string;
  /** Present when kind === 'conversation_action'. */
  action_channel?: string;
  /** Type-discriminated by kind. See worker/src/routes/contactActivity.ts. */
  metadata: Record<string, unknown>;
}

export interface ContactActivityResponse {
  items: ContactActivityItem[];
  has_more: boolean;
  /** Oldest occurred_at in the returned page. Use as `?since=` for next page. */
  oldest_at: string | null;
}

/** One bucket inside ContactExtractionsSummaryResponse.by_kind. */
export interface ContactExtractionSummaryBucket {
  latest: {
    value: string;
    confidence: "high" | "medium" | "low";
    message_id: string;
    conversation_id: string | null;
    source: string;
    model_version: string;
    extracted_at: string;
  };
  count: number;
  all: Array<{
    value: string;
    confidence: "high" | "medium" | "low";
    message_id: string;
    conversation_id: string | null;
    source: string;
    model_version: string;
    extracted_at: string;
  }>;
  /** True when the kind maps to a contacts.<column>. */
  promotable: boolean;
  /** Column name on contacts, or null when not promotable. */
  contact_field: string | null;
  /** True when contact.<column> already equals latest.value (case-insensitive). */
  already_saved: boolean;
}

export interface ContactExtractionsSummaryResponse {
  by_kind: Record<string, ContactExtractionSummaryBucket>;
}
